import React, { useState } from "react";
import { 
  Calendar, Clock, Users, Video, Mic, Check, 
  CalendarPlus, X, Bell, BellOff, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScheduledArena, useScheduledArenas } from "@/hooks/useScheduledArenas";
import { ArenaTypeBadge } from "./ArenaTypeBadge";
import { format, formatDistanceToNow, isToday, isTomorrow, addMinutes } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { addToCalendar, CalendarEvent } from "@/lib/calendarUtils";

interface ScheduledArenasListProps {
  className?: string;
}

export const ScheduledArenasList: React.FC<ScheduledArenasListProps> = ({ className }) => {
  const { scheduledArenas, loading, rsvpToArena, cancelRsvp, createScheduledArena } = useScheduledArenas();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const getDateLabel = (date: Date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEE, MMM d");
  };

  // Group by date
  const groupedArenas = scheduledArenas.reduce((groups, arena) => {
    const date = new Date(arena.scheduled_at);
    const dateKey = format(date, "yyyy-MM-dd");
    if (!groups[dateKey]) {
      groups[dateKey] = { label: getDateLabel(date), arenas: [] };
    }
    groups[dateKey].arenas.push(arena);
    return groups;
  }, {} as Record<string, { label: string; arenas: ScheduledArena[] }>);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Upcoming Arenas
        </h3>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5 rounded-full">
              <CalendarPlus className="w-4 h-4" />
              Schedule
            </Button>
          </DialogTrigger>
          <CreateScheduledArenaModal 
            onClose={() => setIsCreateOpen(false)} 
            onCreate={createScheduledArena}
          />
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : Object.keys(groupedArenas).length === 0 ? (
        <div className="text-center py-8 text-muted-foreground bg-card/50 rounded-2xl">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No upcoming arenas scheduled</p>
          <p className="text-sm">Be the first to schedule one!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedArenas).map(([dateKey, group]) => (
            <div key={dateKey}>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">{group.label}</h4>
              <div className="space-y-3">
                {group.arenas.map((arena) => (
                  <ScheduledArenaCard 
                    key={arena.id} 
                    arena={arena} 
                    onRsvp={rsvpToArena}
                    onCancelRsvp={cancelRsvp}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface ScheduledArenaCardProps {
  arena: ScheduledArena;
  onRsvp: (arenaId: string, status: "going" | "maybe" | "not_going") => Promise<{ error: Error | null }>;
  onCancelRsvp: (arenaId: string) => Promise<{ error: Error | null }>;
}

const ScheduledArenaCard: React.FC<ScheduledArenaCardProps> = ({ arena, onRsvp, onCancelRsvp }) => {
  const [isRsvping, setIsRsvping] = useState(false);
  const scheduledDate = new Date(arena.scheduled_at);

  const handleRsvp = async (status: "going" | "maybe") => {
    setIsRsvping(true);
    const { error } = await onRsvp(arena.id, status);
    if (!error) {
      toast({
        title: status === "going" ? "You're going! 🎉" : "Marked as maybe",
        description: `We'll remind you before ${arena.name} starts`,
      });
    }
    setIsRsvping(false);
  };

  const handleCancelRsvp = async () => {
    setIsRsvping(true);
    const { error } = await onCancelRsvp(arena.id);
    if (!error) {
      toast({
        title: "RSVP cancelled",
        description: "You won't receive reminders for this arena",
      });
    }
    setIsRsvping(false);
  };

  const handleAddToCalendar = (type: 'google' | 'apple') => {
    const event: CalendarEvent = {
      title: `🎈 ${arena.name} - Pop Arena`,
      description: `Join "${arena.name}" arena on Pop!\n\nTopic: ${arena.topic}\n${arena.description || ''}\n\nHosted by: ${arena.creator?.name || 'Someone'}`,
      startTime: scheduledDate,
      endTime: addMinutes(scheduledDate, arena.duration_minutes || 60),
      location: 'Pop App - Live Arenas',
    };
    addToCalendar(event, type);
    toast({
      title: "Added to calendar! 📅",
      description: "Event added with a 15-minute reminder",
    });
  };

  return (
    <div className="p-4 rounded-2xl bg-card border border-border shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold truncate">{arena.name}</h4>
            <ArenaTypeBadge type={arena.arena_type as any} className="scale-90" />
          </div>
          
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {format(scheduledDate, "h:mm a")}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {arena.rsvp_count || 0} going
            </span>
            <span className="flex items-center gap-1">
              {arena.is_voice_only ? (
                <><Mic className="w-3.5 h-3.5" /> Voice</>
              ) : (
                <><Video className="w-3.5 h-3.5" /> Video</>
              )}
            </span>
          </div>

          {arena.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{arena.description}</p>
          )}

          {/* Host info */}
          {arena.creator && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <img 
                src={arena.creator.photo_url || "https://api.dicebear.com/7.x/avataaars/svg?seed=default"} 
                alt="" 
                className="w-5 h-5 rounded-full object-cover"
              />
              <span>Hosted by {arena.creator.name}</span>
            </div>
          )}
        </div>

        {/* RSVP buttons */}
        <div className="flex flex-col gap-2">
          {arena.user_rsvp_status === "going" ? (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-1 text-sm text-green-600 font-medium">
                <Check className="w-4 h-4" />
                Going
              </div>
              
              {/* Add to calendar */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-full text-xs gap-1">
                    <Calendar className="w-3 h-3" />
                    Add to Cal
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleAddToCalendar('google')}>
                    Google Calendar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddToCalendar('apple')}>
                    Apple Calendar / iCal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cancel RSVP */}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelRsvp}
                disabled={isRsvping}
                className="text-xs text-muted-foreground"
              >
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => handleRsvp("going")}
                disabled={isRsvping}
                className="rounded-full bg-gradient-to-r from-pop to-primary"
              >
                RSVP
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRsvp("maybe")}
                disabled={isRsvping}
                className="rounded-full text-xs"
              >
                Maybe
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Time until start */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Starts {formatDistanceToNow(scheduledDate, { addSuffix: true })}
        </span>
        <span className="flex items-center gap-1">
          <Bell className="w-3 h-3" />
          Reminder 15 min before
        </span>
      </div>
    </div>
  );
};

interface CreateScheduledArenaModalProps {
  onClose: () => void;
  onCreate: (arena: any) => Promise<{ data?: any; error: Error | null }>;
}

const CreateScheduledArenaModal: React.FC<CreateScheduledArenaModalProps> = ({ onClose, onCreate }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    topic: "Casual",
    description: "",
    scheduled_at: "",
    scheduled_time: "",
    duration_minutes: 60,
    is_voice_only: false,
    arena_type: "vibe_booth",
  });

  const topics = ["Flirting", "Relationships", "Travel", "Late Night", "Casual", "Adventure", "Food & Dining", "Music"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.scheduled_at || !form.scheduled_time) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    setIsCreating(true);
    const scheduledDateTime = new Date(`${form.scheduled_at}T${form.scheduled_time}`);
    
    const { error } = await onCreate({
      name: form.name,
      topic: form.topic,
      description: form.description || undefined,
      scheduled_at: scheduledDateTime.toISOString(),
      duration_minutes: form.duration_minutes,
      is_voice_only: form.is_voice_only,
      is_video_enabled: !form.is_voice_only,
      arena_type: form.arena_type,
    });

    if (!error) {
      toast({ title: "Arena scheduled! 🎉", description: "Others can now RSVP" });
      onClose();
    } else {
      toast({ title: "Failed to schedule arena", variant: "destructive" });
    }
    setIsCreating(false);
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CalendarPlus className="w-5 h-5 text-primary" />
          Schedule an Arena
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Arena Name *</Label>
          <Input
            placeholder="e.g., Friday Night Vibes"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={50}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date *</Label>
            <Input
              type="date"
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              min={format(new Date(), "yyyy-MM-dd")}
            />
          </div>
          <div>
            <Label>Time *</Label>
            <Input
              type="time"
              value={form.scheduled_time}
              onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
            />
          </div>
        </div>

        <div>
          <Label>Topic</Label>
          <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {topics.map((topic) => (
                <SelectItem key={topic} value={topic}>{topic}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Description (optional)</Label>
          <Textarea
            placeholder="What's this arena about?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            maxLength={200}
            rows={2}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label>Voice-only (no video)</Label>
          <Switch
            checked={form.is_voice_only}
            onCheckedChange={(v) => setForm({ ...form, is_voice_only: v })}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating} className="flex-1 gap-2">
            {isCreating ? "Scheduling..." : "Schedule Arena"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default ScheduledArenasList;
